import { useContext, useState } from 'react';
import { format, isValid } from 'date-fns';
import { shallowEqual } from 'react-redux';

import { Flex, Spacer } from '@zillow/constellation';
import { Label, Tag, TextButton } from '@zillow/constellation-10';

import {
  FeeType,
  useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_getFeesForType,
} from '@zg-rentals/rental-manager-env';

import { useSelector } from '../../../../selectors/useSelector';

import { RECS } from '../../../../constants/RecommendationsConstants';
import RecommendationsLearnMoreContent from '../../../RecommendationsLearnMoreContent';

import numberUtils from '../../../../utils/numberUtils';

import ApplicationsCSEvents from '../../../../model/ClickstreamEvent/ApplicationsCSEvents';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';

import {
  ALLOW_CONTACT_BY_PHONE,
  LEASE_DURATION,
  MODAL_COMPONENT,
  RECEIVE_ZILLOW_APPLICATIONS,
} from '../../../../components/WizardModularListing/Review/constants';
import ContentSectionEditButton from '../../../../components/WizardModularListing/Review/components/ContentSectionEditButton';
import FieldValueDisplay from '../../../../components/WizardModularListing/Review/components/FieldValueDisplay';
import { FOR_RENT_BY_KEY } from '../../../../containers/CreateListingPage/constants';
import ApplicationSampleModal from '../../../Modal/ApplicationSampleModal';

import {
  StyledReviewSection,
  StyledReviewTitleRow,
} from '../../../../components/WizardModularListing/Review/StyledComponents';
import { ReviewSectionTitle } from '../../../../styles/wizard';

import type { FastPostReviewContentProps as Props } from '../interfaces';

const ariaLabels = {
  HIDE_PROPERTY_ADDRESS: 'hide-property-address',
  DATE_AVAILABLE: 'date-available',
  LEASE_DURATION: 'lease-duration',
  ALLOW_CONTACT_BY_PHONE: 'allow-contact-by-phone',
  ZILLOW_APPLICATIONS: 'zillow-applications',
};

const getLeaseDuration = (key: string) => {
  return (LEASE_DURATION as Record<string, string>)[key];
};

const FastPostReviewContent = ({ onEditClick }: Props): JSX.Element => {
  const {
    modularListingState: { canAccessSFCostsAndFees },
  } = useContext(ModularListingContext);

  const {
    currentProperty,
    currentProperty: {
      floorplans,
      forRentBy,
      hideAddress,
      hidePhone,
      isApplicationsAccepted,
      leaseDuration,
      listingAlias,
      restrictions: {
        applications: { isUpdateRestricted: isApplicationsUpdateRestricted },
      },
    },
    user,
  } = useSelector(
    (state) => ({
      appConfig: state?.app?.appConfig,
      cookieEnabledServices: state?.app?.cookieEnabledServices,
      currentProperty: state.properties.currentProperty,
      user: state.user,
    }),
    shallowEqual,
  );

  const [displaySampleModal, setDisplaySampleModal] = useState(false);
  const [displayRecsModal, setDisplayRecsModal] = useState<string>();

  const { data: applicationFee = [] } = useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_getFeesForType(
    {
      feeType: FeeType.APPLICATION_FEE,
      listingAlias,
    },
    {
      enabled: canAccessSFCostsAndFees && listingAlias !== '',
    },
  );

  const feeAmount = applicationFee?.length ? applicationFee?.[0]?.feeAmountMin : null;

  const floorplan = floorplans[0];
  const { dateAvailable: pDateAvailable } = floorplan;

  // after format the day is behind by 1 day
  const dateAvailable = new Date(pDateAvailable);

  const displayDateAvailable =
    pDateAvailable &&
    isValid(dateAvailable) &&
    format(new Date(dateAvailable.setDate(dateAvailable.getDate() + 1)), 'MM/dd/yyyy');
  const displayLeaseDuration = getLeaseDuration(leaseDuration);

  const getCustomValueForApplicationsField = () => {
    if (isApplicationsAccepted !== null) {
      return RECEIVE_ZILLOW_APPLICATIONS[isApplicationsAccepted?.toString()];
    }
  };

  return (
    <div data-testid="FastPostReviewContent">
      <StyledReviewSection data-testid="HidePropertyAddress">
        <StyledReviewTitleRow>
          <ReviewSectionTitle>
            <Label>Hide property address</Label>
            <TextButton
              aria-label={`learn-more-about-${ariaLabels.HIDE_PROPERTY_ADDRESS}`}
              textStyle="body-sm"
              onClick={() => {
                setDisplayRecsModal(RECS.HIDE_STREET_ADDRESS);
              }}
            >
              Learn more
            </TextButton>
          </ReviewSectionTitle>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.HIDE_PROPERTY_ADDRESS}
            value={MODAL_COMPONENT.HideAddressFormField.componentKey}
            onEditClick={onEditClick}
          />
        </StyledReviewTitleRow>
        <FieldValueDisplay value={hideAddress ? 'Yes' : 'No'} showIconPlaceholder={false} />
      </StyledReviewSection>

      <StyledReviewSection data-testid="DateAvailable">
        <StyledReviewTitleRow>
          <Flex display="flex" alignItems="center">
            <Label>Date available</Label>
          </Flex>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.DATE_AVAILABLE}
            value={MODAL_COMPONENT.DateAvailableFormField.componentKey}
            onEditClick={onEditClick}
          />
        </StyledReviewTitleRow>
        <FieldValueDisplay value={displayDateAvailable} showIconPlaceholder={false} />
      </StyledReviewSection>

      <StyledReviewSection data-testid="LeaseDuration">
        <StyledReviewTitleRow>
          <Flex display="flex" alignItems="center">
            <Label required={true}>Lease duration</Label>
          </Flex>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.LEASE_DURATION}
            value={MODAL_COMPONENT.LeaseDurationFieldSet.componentKey}
            onEditClick={onEditClick}
          />
        </StyledReviewTitleRow>
        <FieldValueDisplay value={displayLeaseDuration} showIconPlaceholder={false} />
      </StyledReviewSection>

      {forRentBy !== FOR_RENT_BY_KEY.AGENT && (
        <StyledReviewSection data-testid="AllowContactByPhone">
          <StyledReviewTitleRow>
            <ReviewSectionTitle>
              <Label>Allow renters to contact you by phone</Label>
              <TextButton
                aria-label={`learn-more-about-${ariaLabels.ALLOW_CONTACT_BY_PHONE}`}
                textStyle="body-sm"
                onClick={() => {
                  setDisplayRecsModal(RECS.HIDE_PHONE);
                }}
              >
                Learn more
              </TextButton>
            </ReviewSectionTitle>

            <ContentSectionEditButton
              buttonFieldName={ariaLabels.ALLOW_CONTACT_BY_PHONE}
              value={MODAL_COMPONENT.AllowContactByPhoneFieldSet.componentKey}
              onEditClick={onEditClick}
            />
          </StyledReviewTitleRow>

          <FieldValueDisplay
            value={(ALLOW_CONTACT_BY_PHONE as Record<string, string>)[String(!hidePhone)]}
            showIconPlaceholder={false}
          />
        </StyledReviewSection>
      )}

      {isApplicationsUpdateRestricted === false && (
        <StyledReviewSection data-testid="ZillowApplications">
          <StyledReviewTitleRow>
            <ReviewSectionTitle>
              <Label>Accept Zillow online applications</Label>
              <Flex display="flex" flexWrap="wrap" justifyContent="center">
                <Tag tone="info" css={{ marginRight: '200' }}>
                  Free
                </Tag>
                <TextButton
                  aria-label={`see-sample-${ariaLabels.ZILLOW_APPLICATIONS}`}
                  textStyle="body-sm"
                  onClick={() => {
                    setDisplaySampleModal(true);
                    ApplicationsCSEvents.ClickSampleApplicationLinkOnPage({
                      currentProperty,
                      user,
                    });
                  }}
                  data-eventid={ApplicationsCSEvents.ClickSampleApplicationLinkOnPage.data_event_id}
                >
                  Sample
                </TextButton>
              </Flex>
            </ReviewSectionTitle>

            <ContentSectionEditButton
              buttonFieldName={ariaLabels.ZILLOW_APPLICATIONS}
              value={MODAL_COMPONENT.AcceptZillowApplicationsFieldSet.componentKey}
              onEditClick={onEditClick}
              allowFeedEdit={true}
            />
          </StyledReviewTitleRow>

          <FieldValueDisplay value={getCustomValueForApplicationsField()} showIconPlaceholder={false} />
          {canAccessSFCostsAndFees && feeAmount !== null && !isApplicationsAccepted && (
            <Spacer marginTop="sm">
              <FieldValueDisplay
                value={`Application fee: ${numberUtils.asCurrency(feeAmount, feeAmount % 1 != 0 ? '$0,0.00' : '$0,0')}`}
                showIconPlaceholder={false}
              />
            </Spacer>
          )}
        </StyledReviewSection>
      )}

      {displayRecsModal && (
        <RecommendationsLearnMoreContent
          onClose={() => setDisplayRecsModal('')}
          recommendationType={displayRecsModal}
        />
      )}
      {displaySampleModal && <ApplicationSampleModal onClose={() => setDisplaySampleModal(false)} />}
    </div>
  );
};

export default FastPostReviewContent;
